
import { HouseholdLayout } from "@/components/layouts/HouseholdLayout";
import { IconLogo } from "@/components/IconLogo";
import { useParams, Link, Outlet } from "react-router-dom";
import { useGetRecipeQuery, useDeleteRecipeMutation } from "../api";
import { useGetFavoritesQuery } from "../favoritesApi";
import { Stack, Grid, Chip, Skeleton } from "@mui/joy";
import { PageHeader } from "@/components/PageHeader";
import { AspectRatio, Box, Button, Typography } from "@mui/joy";
import { ExternalLinkButton } from "@/components/ExternalLinkButton";
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import OndemandVideoOutlinedIcon from '@mui/icons-material/OndemandVideoOutlined';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { useState, useEffect, useMemo } from "react";
import { FavoriteButton } from "../components/FavoriteButton";
import { capitalizeFirstLetter } from "@/utils/strings";
import { RoundNumber } from "@/components/RoundNumber";
import { DeleteAction } from "@/components/DeleteAction";
import { useCrudHouseholdMutation } from "@/hooks/useCrudHouseholdMutation";
import { ApiError } from "@/components/ApiError";
import { useTheme } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import MenuItem from "@mui/joy/MenuItem";
import { TabsSegmented } from "@/components/TabsSegmented";
import { TabPanel } from "@mui/joy";
import { isMatchedIngredient } from "../utils/ingredients";
import { NutritionPerServing } from "../components/NutritionPerServing";
import { IngredientNutritionTooltip } from "../components/IngredientNutritionTooltip";
import { calculateNutrition } from "../utils/nutrition";
import { formatAmount, formatUnit } from "@/features/units/amounts";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import { AddToMealPlanModal } from "../components/AddToMealPlanModal";
import { convertUnit } from "@/features/units/conversion";
import { PortionPicker } from "../components/PortionPicker";
import { useGetProfileQuery } from "@/features/onboarding/api";
import { useGetHouseholdQuery } from "@/features/households/api";
import { TagsList } from "@/features/tags/components/TagsList";
import { CategoriesList } from "@/features/categories/components/CategoriesList";
import { UnitsToggleButton } from "@/features/units/components/UnitsToggleButton";
import { FEATURE_FLAGS } from "@/featureFlags";
import { useStableImageUrls } from "@/features/recipes/hooks/useStableImageUrls";
import { TriedBadge } from "../components/TriedBadge";

const POLLING_INTERVAL = 1000;

export function ViewRecipePage() {
  const { householdId, id } = useParams();
  const [pollingInterval, setPollingInterval] = useState(0);
  
  const { data, isLoading, error } = useGetRecipeQuery(
    { householdId, id },
    { pollingInterval }
  );

  useEffect(() => {
    setPollingInterval(data?.data?.processed === false ? POLLING_INTERVAL : 0);
  }, [data?.data?.processed]);
    
  return (
    <HouseholdLayout>     
      {isLoading ? 'Loading...' : error ? <ApiError error={error} /> : <ViewRecipe recipe={data.data} />}      
    </HouseholdLayout>
  );
}

function ViewRecipe({ recipe }) {  
  const { householdId } = useParams();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const useDelete = useCrudHouseholdMutation(householdId, useDeleteRecipeMutation);
  const [showAddToMealPlanModal, setShowAddToMealPlanModal] = useState(false);

  const favorites = useGetFavoritesQuery({ householdId });

  const isFavorite = useMemo(() => {
    return favorites.data?.data?.includes(recipe.id) ?? false;
  }, [favorites.data, recipe.id]);

  return (    
    <>
      <PageHeader title={recipe.name} subtitle={recipe.description} moreActions={isMobile ? <>                              
          <Link to={`./edit`}><MenuItem><ModeEditOutlinedIcon /> Edit</MenuItem></Link>        
          <DeleteAction type="menu-item" id={recipe.id} name={recipe.name} useDelete={useDelete} redirectOnSuccess={true} />
        </> : null}>        
        {!isMobile && (
          <>
            <DeleteAction type="button" id={recipe.id} name={recipe.name} useDelete={useDelete} redirectOnSuccess={true} />            
            <Link to={`./edit`}><Button startDecorator={<ModeEditOutlinedIcon />}>Edit</Button></Link>        
          </>
        )}                
      </PageHeader>
      <Stack direction="row" gap={1} sx={{ mt: 2, mb: { xs: 3, md: 4 }, flexWrap: 'wrap', alignItems: 'center' }}>        
        <CategoriesList categories={recipe.categories} gap={1} size="md" />
        {recipe.tried && (
          <Link to={`/h/${householdId}/recipes?tried=true`} style={{ textDecoration: 'none' }}>
            <Chip
              size="md"
              variant="soft"
              color="neutral"
              startDecorator={<RestaurantOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{ '&:hover': { backgroundColor: 'neutral.300' } }}
            >
              Tried
            </Chip>
          </Link>
        )}
        <TagsList tags={recipe.tags} gap={1} size="md" />
      </Stack>      
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ mb: 2 }}>
        <Button startDecorator={<CalendarMonthIcon />} onClick={() => setShowAddToMealPlanModal(true)} sx={{ width: { xs: '100%', sm: 'auto' } }}>Add to Meal Plan</Button>
        <Link to={`./add-to-shopping-list`}><Button variant="outlined" startDecorator={<AddShoppingCartIcon />} sx={{ width: { xs: '100%', sm: 'auto' } }}>Add to Shopping List</Button></Link>
        {FEATURE_FLAGS.mealTracking && (
          <Link to={`./log-meal`}><Button variant="outlined" startDecorator={<RestaurantOutlinedIcon />} sx={{ width: { xs: '100%', sm: 'auto' } }}>Track as Eaten</Button></Link>
        )}
      </Stack>
      <Grid container spacing={{ xs: 0, md: 4 }} direction={{ xs: 'column', md: 'row' }}>
        <Grid xs={12} md={6}>
          <Stack direction="column" gap={3}>
            <Images images={recipe.images}>            
              <TriedBadge tried={recipe.tried} />
              <FavoriteButton
                recipeId={recipe.id}
                isFavorite={isFavorite}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 10,            
                }}
              />
          </Images>
            <Stack direction="column" gap={{ xs: 1, sm: 2, md: 1, lg: 2 }}>
              <Stack direction="row" gap={{ xs: 1, sm: 2, md: 1, lg: 2 }}>
                <PrettyBox title="Serves" value={recipe.servings} />
                <PrettyBox title="Prep" value={recipe.prep_time + ' min'} />
                <PrettyBox title="Cook" value={recipe.cook_time + ' min'} />
                <PrettyBox title="Total" value={recipe.prep_time + recipe.cook_time + ' min'} />
              </Stack>
              <NutritionPerServing recipe={recipe} />
            </Stack>            
            <Stack direction="column" gap={1}>
              {(recipe.url || recipe.video_url) && <Stack direction="column" gap={1}>
                <Typography level="title-md">Original Sources</Typography>
                {recipe.url && <ExternalLinkButton url={recipe.url} fullWidth={isMobile} />}
                {recipe.video_url && <ExternalLinkButton url={recipe.video_url} fullWidth={isMobile} icon={<OndemandVideoOutlinedIcon />} />}            
              </Stack>}
            </Stack>
            {!isMobile && recipe.instruction_groups.length > 0 && <Instructions groups={recipe.instruction_groups} />}
            {isMobile && <MobileTabs recipe={recipe} />}
          </Stack>
        </Grid>
        {!isMobile && (
        <Grid xs={12} md={6}>
          <Stack direction="column" gap={5}>
              <Ingredients groups={recipe.ingredient_groups} originalServings={recipe.servings} isProcessed={recipe.processed} />
              {recipe.equipment.length > 0 && <Equipment equipment={recipe.equipment} />}                   
            </Stack>         
          </Grid>       
        )}
      </Grid>
      <AddToMealPlanModal
        open={showAddToMealPlanModal}
        onClose={() => setShowAddToMealPlanModal(false)}
        recipe={recipe}
        householdId={householdId}
      />
      <Outlet />
    </>
  );
}

function MobileTabs({ recipe }) {
  const hasInstructions = recipe.instruction_groups.length > 0;
  const hasEquipment = recipe.equipment.length > 0;
  const tabs = ['Ingredients', ...(hasInstructions ? ['Instructions'] : []), ...(hasEquipment ? ['Equipment'] : [])];
  const [activeTab, setActiveTab] = useState(0);
  const instructionsTab = 1;
  const equipmentTab = hasInstructions ? 2 : 1;
  return (
      <TabsSegmented tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} sx={{ mb: 2, mt: 1, mx: -0.5 }} size="lg" fullWidth={true}>
      <TabPanel value={0}><Ingredients groups={recipe.ingredient_groups} hideName={true} originalServings={recipe.servings} isProcessed={recipe.processed} /></TabPanel>
      {hasInstructions && (
        <TabPanel value={instructionsTab}>
          <Instructions groups={recipe.instruction_groups} hideName={true} />
        </TabPanel>
      )}
      {hasEquipment && <TabPanel value={equipmentTab}><Equipment equipment={recipe.equipment} hideName={true} /></TabPanel>}
    </TabsSegmented>    
  );
}

function Ingredients({ groups, hideName, originalServings, isProcessed }) {
  const { householdId } = useParams();
  const { data: profileData } = useGetProfileQuery();
  const { data: householdData } = useGetHouseholdQuery({ householdId });

  const preferredUnits = profileData?.data?.profile?.units || 'metric';
  const [useOriginalUnits, setUseOriginalUnits] = useState(false);
  const [scalingFactor, setScalingFactor] = useState(1);
  const preferredServings = householdData?.data?.members || originalServings;
  const totalKcal = useMemo(() => calculateNutrition({ ingredient_groups: groups })?.kcal ?? 0, [groups]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: hideName ? 0 : 1 }}>
        {!hideName && <Typography level="h3">Ingredients</Typography>}
        <Stack direction="row" gap={1} sx={{ mb: { xs: 1, sm: 0 } }}>
          <PortionPicker 
            originalServings={originalServings}
            preferredServings={preferredServings}
            onServingsChange={setScalingFactor}
          />
          <UnitsToggleButton value={useOriginalUnits} onChange={setUseOriginalUnits} />
        </Stack>
      </Stack>
      <Stack direction="column" gap={1} sx={{ mt: hideName ? 0 : 1 }}>
        {groups.map((group, index) =>
          <IngredientGroup key={index} group={group} showName={groups.length > 1 || groups[0].name !== 'Ingredients'} preferredUnits={preferredUnits} useOriginalUnits={useOriginalUnits} scalingFactor={scalingFactor} isProcessed={isProcessed} totalKcal={totalKcal} />
        )}
      </Stack>
    </Box>
  );
}

function IngredientGroup({ group, showName, preferredUnits, useOriginalUnits, scalingFactor, isProcessed, totalKcal }) {
  return (
    <Stack direction="column" gap={1} sx={{ mt: showName ? 2 : 1 }}>
      {showName && <Typography level="title-lg">{group.name}</Typography>}
      <Stack direction="column" gap={1}>      
        {<table style={{ borderCollapse: 'collapse' }}>
          <tbody>
            {group.ingredients.map((ingredient, index) => {
              const matchedIngredient = ingredient.closest_ingredients?.[0];
              const scaledAmount = ingredient.amount * scalingFactor;
              const display = useOriginalUnits 
                ? { amount: scaledAmount, unit: ingredient.unit }
                : convertUnit(scaledAmount, ingredient.unit, preferredUnits, matchedIngredient);
              return (
                <tr key={index} style={{ borderBottom: (index < group.ingredients.length - 1) ? '1px solid var(--joy-palette-neutral-200)' : 'none' }}>
                  <td style={{ padding: '6px 8px 6px 0' }}>
                    <IngredientNutritionTooltip ingredient={ingredient} amount={display.amount} unit={display.unit} totalKcal={totalKcal * scalingFactor}>
                      <Typography level="body-md" sx={{ fontWeight: '300' }}>{capitalizeFirstLetter(ingredient.name)}</Typography>
                    </IngredientNutritionTooltip>
                  </td>
                  <td style={{ padding: '6px 6px 6px 0', textAlign: 'right', whiteSpace: 'nowrap' }}><Typography level="body-md" sx={{ fontWeight: '300' }}>{formatAmount(display.amount, display.unit)}</Typography></td>
                  <td style={{ padding: '6px 8px 6px 0' }}><Typography level="body-md" sx={{ fontWeight: '300' }}>{formatUnit(display.unit)}</Typography></td>
                  <td style={{ padding: '4px' }}><IngredientMatch ingredient={ingredient} isProcessed={isProcessed} /></td>
                </tr>
              );
            })}          
          </tbody>
        </table>}
        
      </Stack>
    </Stack>
  );
}

function IngredientMatch({ ingredient, isProcessed }) {    
  const theme = useTheme();  
  const xs = useMediaQuery(theme.breakpoints.down('sm'));
  const md = useMediaQuery(theme.breakpoints.only('md'));
  const showTiny = xs || md;

  if (isProcessed === false) {
    return <Skeleton variant="rectangular" width={showTiny ? 8 : Math.min(150, ingredient.name.length * 7)} height={18} sx={{ borderRadius: '24px' }} />;
  }

  if (!ingredient || !ingredient.closest_ingredients || ingredient.closest_ingredients.length === 0) {
    return null;
  }

  const isMatched = isMatchedIngredient(ingredient);
  return <Chip size="sm" color={isMatched ? "success" : "danger"} sx={{ px: showTiny ? 0.5 : 0.75 }}>{showTiny ? '' : ingredient.closest_ingredients[0].name}</Chip>;  
}

function Equipment({ equipment, hideName }) {
  return (
    <Box>
      {!hideName && <Typography level="h3">Equipment</Typography>}
      <Stack direction="column" gap={1} sx={{ mt: 1 }}>
        {equipment.map((item, index) => <Typography key={index} level="body-md" sx={{ fontWeight: '300' }}>{item.name}</Typography>)}
      </Stack>
    </Box>
  );
}

function Instructions({ groups, hideName }) {
  return (
    <Box>
      {!hideName && <Typography level="h3">Instructions</Typography>}
      <Stack direction="column" gap={2} mt={hideName ? 0 : 1}>
        {groups.map((group, index) => <InstructionGroup key={index} group={group} showName={groups.length > 1 || groups[0].name !== 'Instructions'} />)}
      </Stack>
    </Box>
  );
}

function InstructionGroup({ group, showName }) {
  return (
    <Stack direction="column" gap={1} sx={{ mt: showName ? 1 : 0 }}>
      {showName && <Typography level="title-lg">{group.name}</Typography>}
      <Stack direction="column" gap={2} mt={0.5}>
        {group.instructions.map((instruction, index) => <Instruction key={index} index={index} instruction={instruction} />)}        
      </Stack>
    </Stack>
  );
}

function Instruction({ index, instruction }) {
  return (
    <Stack direction="row" gap={1.5} sx={{ backgroundColor: 'var(--joy-palette-neutral-100)', p: 1.5, py: 1.25, borderRadius: 'sm' }}>
      <RoundNumber number={index + 1} size="sm" />
      <Typography level="body-md" sx={{ fontWeight: '300' }}>{instruction.step}</Typography>
    </Stack>    
  );
}

function PrettyBox({ title, value }) {
  return (
    <Box sx={{ flex: 1, backgroundColor: 'var(--joy-palette-neutral-100)', px: 1.5, py: 1, borderRadius: 'sm' }}>
      <Typography level="title-md" sx={{ fontWeight: '500' }}>{title}</Typography>
      <Typography level="body-md" sx={{ fontWeight: '300' }}>{value}</Typography>
    </Box>
  );
}

function Images({ images, children }) {  
  const [activeImage, setActiveImage] = useState(0);
  const stableImages = useStableImageUrls(images);
  
  return (
    <Stack direction="column" gap={2}>
      <AspectRatio ratio={6 / 4} sx={{ borderRadius: 'md' }}>
        {stableImages.length > 0 ? 
          <img
            src={stableImages[activeImage]?.url}
            srcSet={stableImages[activeImage]?.srcset}
            sizes="(max-width: 900px) 100vw, 800px"
            style={{ objectFit: 'cover' }}
          /> :
          <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', opacity: 0.2 }}><IconLogo size={"25%"} /></Box> 
        }
        { children }
      </AspectRatio>

      <Stack direction="row" gap={1} sx={{ overflowX: 'auto' }}>
        {stableImages.map((image, index) => (
          <AspectRatio 
            key={image.file_name ? `${image.file_name}${image.ext}` : image.url}
            ratio={6 / 4}
            sx={{
              width: '120px',
              borderRadius: 'md',
              cursor: 'pointer', 
              border: index === activeImage ? '2px solid var(--joy-palette-primary-500)' : 'none'
            }}
            onClick={() => setActiveImage(index)}
          >
            <img src={image.url} srcSet={image.srcset} sizes="120px" style={{ objectFit: 'cover' }} />
          </AspectRatio>
        ))}
      </Stack>
    </Stack>
  );
}
